import { supabase } from '../lib/supabase';
import { logAuditEvent } from './auditLogService';
import JSZip from 'jszip';
import Papa from 'papaparse';

/**
 * Privacy Service
 * Handles GDPR/CCPA privacy requests
 */

/**
 * Create privacy request
 */
export async function createPrivacyRequest({
  requestType,
  rectificationData = null
}) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Calculate deadline (30 days for GDPR/CCPA)
    const deadlineDate = new Date();
    deadlineDate?.setDate(deadlineDate?.getDate() + 30);
    
    const { data, error } = await supabase
      ?.from('privacy_requests')
      ?.insert({
        user_id: user?.id,
        request_type: requestType,
        rectification_data: rectificationData,
        deadline_date: deadlineDate?.toISOString()?.split('T')?.[0]
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    // Log audit event
    await logAuditEvent({
      actionType: 'privacy_request_created',
      actionDescription: `Privacy request created: ${requestType}`,
      entityType: 'privacy_request',
      entityId: data?.id,
      severity: 'info'
    });
    
    return { success: true, data };
  } catch (error) {
    console.error('Create privacy request error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get user's privacy requests
 */
export async function getPrivacyRequests() {
  try {
    const { data, error } = await supabase
      ?.from('privacy_requests')
      ?.select('*')
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Get privacy requests error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Export user data (Right to Access)
 */
export async function exportUserData(format = 'json') {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Call database function to export data
    const { data, error } = await supabase?.rpc('export_user_data', {
      p_user_id: user?.id
    });
    
    if (error) throw error;
    
    // Log audit event
    await logAuditEvent({
      actionType: 'data_export',
      actionDescription: `User data exported in ${format} format`,
      entityType: 'user',
      entityId: user?.id,
      severity: 'info'
    });
    
    // Generate download based on format
    if (format === 'json') {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL?.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my_data_${new Date()?.toISOString()?.split('T')?.[0]}.json`;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      window.URL?.revokeObjectURL(url);
    } else if (format === 'csv') {
      const zip = new JSZip();
      
      // Convert each data type to CSV
      Object.keys(data)?.forEach(key => {
        if (key !== 'exported_at' && data?.[key]) {
          const csvData = Array.isArray(data?.[key]) ? data?.[key] : [data?.[key]];
          const csv = Papa?.unparse(csvData);
          zip?.file(`${key}.csv`, csv);
        }
      });
      
      // Generate ZIP file
      const zipBlob = await zip?.generateAsync({ type: 'blob' });
      const url = window.URL?.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my_data_${new Date()?.toISOString()?.split('T')?.[0]}.zip`;
      document.body?.appendChild(a);
      a?.click();
      document.body?.removeChild(a);
      window.URL?.revokeObjectURL(url);
    }
    
    return { success: true };
  } catch (error) {
    console.error('Export user data error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Request account deletion (Right to Erasure)
 */
export async function requestAccountDeletion() {
  try {
    const result = await createPrivacyRequest({
      requestType: 'erasure'
    });
    
    return result;
  } catch (error) {
    console.error('Request account deletion error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Cancel account deletion (within grace period)
 */
export async function cancelAccountDeletion(requestId) {
  try {
    const { data, error } = await supabase
      ?.from('privacy_requests')
      ?.update({
        request_status: 'rejected',
        rejection_reason: 'Cancelled by user within grace period'
      })
      ?.eq('id', requestId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    await logAuditEvent({
      actionType: 'account_deletion_cancelled',
      actionDescription: 'User cancelled account deletion request',
      entityType: 'privacy_request',
      entityId: requestId,
      severity: 'info'
    });
    
    return { success: true, data };
  } catch (error) {
    console.error('Cancel account deletion error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get user consents
 */
export async function getUserConsents() {
  try {
    const { data, error } = await supabase
      ?.from('user_consents')
      ?.select('*')
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Get user consents error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Update consent
 */
export async function updateConsent({
  consentType,
  consentGiven,
  consentText,
  consentVersion
}) {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    // For anonymous users, consent is stored in localStorage only
    // Database storage is only for authenticated users for compliance tracking
    if (!user) {
      return { data: null, error: null }; // Silent success for anonymous users
    }
    
    // Get IP and user agent
    const ipAddress = await fetch('https://api.ipify.org?format=json')?.then(res => res?.json())?.then(data => data?.ip)?.catch(() => 'unknown');
    const userAgent = navigator?.userAgent || 'unknown';
    
    const { data, error } = await supabase
      ?.from('user_consents')
      ?.upsert({
        user_id: user?.id,
        consent_type: consentType,
        consent_given: consentGiven,
        consent_text: consentText,
        consent_version: consentVersion,
        ip_address: ipAddress,
        user_agent: userAgent,
        granted_at: consentGiven ? new Date()?.toISOString() : null,
        revoked_at: !consentGiven ? new Date()?.toISOString() : null
      }, {
        onConflict: 'user_id,consent_type,consent_version'
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    await logAuditEvent({
      actionType: 'consent_updated',
      actionDescription: `Consent ${consentGiven ? 'granted' : 'revoked'} for ${consentType}`,
      entityType: 'consent',
      entityId: data?.id,
      severity: 'info'
    });
    
    return { success: true, data };
  } catch (error) {
    console.error('Update consent error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get admin privacy requests (admin only)
 */
export async function getAdminPrivacyRequests(filters = {}) {
  try {
    let query = supabase
      ?.from('privacy_requests')
      ?.select(`
        *,
        user:user_id (email, full_name)
      `)
      ?.order('created_at', { ascending: false });
    
    if (filters?.status) {
      query = query?.eq('request_status', filters?.status);
    }
    
    if (filters?.requestType) {
      query = query?.eq('request_type', filters?.requestType);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    console.error('Get admin privacy requests error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Process privacy request (admin only)
 */
export async function processPrivacyRequest(requestId, action, notes = '') {
  try {
    const updates = {
      request_status: action === 'approve' ? 'completed' : 'rejected',
      admin_notes: notes,
      completed_at: action === 'approve' ? new Date()?.toISOString() : null,
      rejection_reason: action === 'reject' ? notes : null
    };
    
    const { data, error } = await supabase
      ?.from('privacy_requests')
      ?.update(updates)
      ?.eq('id', requestId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    await logAuditEvent({
      actionType: 'privacy_request_processed',
      actionDescription: `Privacy request ${action}d`,
      entityType: 'privacy_request',
      entityId: requestId,
      severity: 'warning'
    });
    
    return { success: true, data };
  } catch (error) {
    console.error('Process privacy request error:', error);
    return { success: false, error: error?.message };
  }
}